/* eslint-disable camelcase */
import React, { useState, useContext, useMemo } from "react"
import { Box, Typography, Autocomplete, TextField, Switch, FormControlLabel, FormGroup, Paper } from "@mui/material"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"

import { showSuccess, showError, showWarning } from "./Crear/utils/alertUtils"
import dayjs from "./Crear/utils/dayjsConfig"
import ModalPlanificacionPaquetes from "./Crear/components/ModalPlanificacionPaquetes"
import ModalEjecucionTarea from "./Crear/components/ModalEjecucionTarea"

import FullCalendar from "@fullcalendar/react"
import esLocale from "@fullcalendar/core/locales/es"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid"

import { useQuery, useMutation, api } from "../../api"
import { GlobalContext } from "../../contexts/GlobalContext"
import Swal from "sweetalert2"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
  "& .fc-theme-standard td, & .fc-theme-standard th": {
    borderColor: "#e0e0e0",
  },
  "& .fc-col-header-cell-cushion": {
    fontWeight: "bold",
    color: "#196C87",
    padding: "8px",
  },
  "& .fc-toolbar-title": {
    color: "#196C87",
    fontWeight: "bold",
    textTransform: "capitalize",
  },
  "& .fc-button-primary": {
    backgroundColor: "#196C87 !important",
    borderColor: "#196C87 !important",
  },
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const AgendaUnificada = () => {
  const { authState } = useContext(GlobalContext)

  const [modalCrearOpen, setModalCrearOpen] = useState(false)
  const [modalEjecucionOpen, setModalEjecucionOpen] = useState(false)
  const [slotSeleccionado, setSlotSeleccionado] = useState(null)
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null)

  const [soloOcupados, setSoloOcupados] = useState(true)
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([])

  // OPTIMIZACIÓN: Rangos de fecha dinámicos para la consulta
  const [rangoFechas, setRangoFechas] = useState({
    fecha_inicio: null,
    fecha_fin: null,
  })

  const { data: usuariosResponse, isLoading: loadingUsuarios } = useQuery({
    queryKey: ["getAllUsuariosAgenda"],
    queryFn: async () => (await api.get("/Agenda/getAllUsuarios")).data,
    refetchOnWindowFocus: false,
  })

  const recursosBase = useMemo(() => {
    const rawData = usuariosResponse?.data?.data || usuariosResponse?.data || []
    return rawData.map((u) => ({
      id: String(u.usrcodigo || u.id).trim(),
      title: String(u.usrnombre || u.title).trim(),
    }))
  }, [usuariosResponse])

  const recursosParaCalendario = useMemo(() => {
    if (usuariosSeleccionados.length > 0) {
      const idsSeleccionados = usuariosSeleccionados.map((u) => u.id)
      return recursosBase.filter((r) => idsSeleccionados.includes(r.id))
    }
    return recursosBase
  }, [recursosBase, usuariosSeleccionados])

  // OPTIMIZACIÓN: React Query ahora se refresca automáticamente cada vez que cambia el rango
  const {
    data: eventosResponse,
    isLoading: loadingEventos,
    refetch: refetchEventos,
  } = useQuery({
    queryKey: ["getAllEventsCalendar", rangoFechas.fecha_inicio, rangoFechas.fecha_fin],
    queryFn: async () => {
      if (!rangoFechas.fecha_inicio || !rangoFechas.fecha_fin) return []
      const response = await api.get("/Agenda/getAllEventsCalendar", {
        params: {
          fecha_inicio: rangoFechas.fecha_inicio,
          fecha_fin: rangoFechas.fecha_fin,
        },
      })
      return response.data
    },
    enabled: !!rangoFechas.fecha_inicio && !!rangoFechas.fecha_fin,
    refetchOnWindowFocus: false,
  })

  const eventosCrudos = eventosResponse?.data || []

  const eventosFormateados = useMemo(() => {
    if (!Array.isArray(eventosCrudos)) return []

    return eventosCrudos.map((ev) => {
      let finalStart = ev.start
      let finalEnd = ev.end

      const fechaPuraStr = ev.eventofecha || ev.extendedProps?.eventofecha || ""
      const horaIniStr = ev.eventohorainicio || ev.extendedProps?.eventohorainicio || ev.start || ""
      const horaFinStr = ev.eventohorafin || ev.extendedProps?.eventohorafin || ev.end || ""

      if (fechaPuraStr.includes("T") || fechaPuraStr.includes(" ")) {
        const fechaSolo = fechaPuraStr.split(/[T ]/)[0]

        if (horaIniStr.includes("T") || horaIniStr.includes(" ")) {
          const horaSolo = horaIniStr.split(/[T ]/)[1].substring(0, 8)
          finalStart = `${fechaSolo}T${horaSolo}`
        }

        if (horaFinStr.includes("T") || horaFinStr.includes(" ")) {
          const horaSoloFin = horaFinStr.split(/[T ]/)[1].substring(0, 8)
          finalEnd = `${fechaSolo}T${horaSoloFin}`
        }
      }

      return {
        ...ev,
        start: finalStart,
        end: finalEnd,
        resourceId: String(ev.resourceId || ev.usrcodigo || ev.extendedProps?.usrcodigo || "").trim(),
      }
    })
  }, [eventosCrudos])

  const { mutateAsync: moverEvento } = useMutation({
    mutationFn: async (payload) => (await api.post("/Agenda/updateEventoDragDrop", payload)).data,
    onSuccess: () => {
      showSuccess("Agenda actualizada exitosamente")
      refetchEventos()
    },
    onError: (error) => {
      showError(error?.response?.data?.message || "Error al modificar el evento")
      refetchEventos()
    },
  })

  // CORRECCIÓN: Mutación blindada con Optional Chaining (?.) para asegurar actualización
  const { mutateAsync: eliminarEvento } = useMutation({
    mutationFn: async (payload) => (await api.post("/Agenda/deleteEventosPlanificados", payload)).data,
    onSuccess: (res) => {
      showSuccess(res?.message || "Tarea eliminada correctamente")
      refetchEventos()
    },
    onError: (error) => {
      showError(error?.response?.data?.message || "No se pudo eliminar la tarea")
    },
  })

  const handleDateSelect = (selectInfo) => {
    setSlotSeleccionado({
      usuarioId: selectInfo.resource ? String(selectInfo.resource.id).trim() : null,
      usuarioNombre: selectInfo.resource ? selectInfo.resource.title : null,
      fecha: dayjs(selectInfo.start).format("YYYY-MM-DD"),
      hora: dayjs(selectInfo.start).format("HH:mm"),
    })
    setModalCrearOpen(true)
  }

  const handleEventClick = (clickInfo) => {
    setEventoSeleccionado(clickInfo.event)
    setModalEjecucionOpen(true)
  }

  const handleEventDropOrResize = async (info) => {
    const { event, oldEvent, revert } = info

    if (
      event.extendedProps.tecnicoRequerido &&
      oldEvent &&
      event.getResources()[0]?.id !== oldEvent.getResources()[0]?.id
    ) {
      showWarning("El cliente solicitó a este técnico específicamente. No puede reasignarlo.")
      revert()
      return
    }

    const nuevoInicio = dayjs(event.start).format("YYYY-MM-DDTHH:mm:ss")
    const nuevoFin = dayjs(event.end).format("YYYY-MM-DDTHH:mm:ss")
    const nuevoUsrcodigo = String(event.getResources()[0]?.id || event.extendedProps?.usrcodigo || "").trim()

    try {
      await moverEvento({
        eventocodigo: event.id,
        nuevo_usrcodigo: nuevoUsrcodigo,
        nuevo_inicio: nuevoInicio,
        nuevo_fin: nuevoFin,
      })
    } catch (error) {
      revert()
    }
  }

  const handleRequestDelete = (e, eventId) => {
    e.stopPropagation()

    Swal.fire({
      title: "¿Eliminar esta tarea?",
      text: "El sistema comprobará que no tenga historial de bitácora antes de proceder.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await eliminarEvento({ eventocodigo: eventId })
      }
    })
  }

  return (
    <ThemeProvider theme={theme}>
      <CustomBackdrop isLoading={loadingUsuarios || loadingEventos} />
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            Torre de Control: Planificación y Ejecución
          </Typography>
        </Box>

        <StyledRoot>
          <Paper
            elevation={1}
            sx={{ p: 2, mb: 2, display: "flex", gap: 3, alignItems: "center", bgcolor: "#f8f9fa", borderRadius: 2 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Autocomplete
                multiple
                options={recursosBase}
                getOptionLabel={(option) => option.title}
                value={usuariosSeleccionados}
                onChange={(event, newValue) => setUsuariosSeleccionados(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filtrar agenda por Usuarios específicos..."
                    placeholder="Ej: Cristian Tandazo"
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: "white" }}
                  />
                )}
                noOptionsText="No se encontraron usuarios"
              />
            </Box>

            <FormGroup>
              <FormControlLabel
                control={
                  <Switch checked={soloOcupados} onChange={(e) => setSoloOcupados(e.target.checked)} color="primary" />
                }
                label={
                  <Typography variant="body2" fontWeight="bold" color="textSecondary">
                    Ocultar columnas sin tareas hoy
                  </Typography>
                }
              />
            </FormGroup>
          </Paper>

          <Box sx={{ flexGrow: 1, bgcolor: "white", p: 2, borderRadius: 2, boxShadow: 1 }}>
            <FullCalendar
              schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
              plugins={[resourceTimeGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="resourceTimeGridDay"
              locales={[esLocale]}
              locale="es"
              resources={recursosParaCalendario}
              filterResourcesWithEvents={soloOcupados && usuariosSeleccionados.length === 0}
              events={eventosFormateados}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "resourceTimeGridDay,timeGridWeek",
              }}
              height="70vh"
              slotMinTime="07:00:00"
              slotMaxTime="19:00:00"
              allDaySlot={false}
              expandRows={true}
              slotDuration="00:15:00"
              selectable={true}
              selectMirror={true}
              editable={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDropOrResize}
              eventResize={handleEventDropOrResize}
              // === OPTIMIZACIÓN DE RANGOS: Captura el rango dinámico visible ===
              datesSet={(dateInfo) => {
                const nuevoInicio = dayjs(dateInfo.start).format("YYYY-MM-DD")
                const nuevoFin = dayjs(dateInfo.end).format("YYYY-MM-DD")

                // FRENO DE BUCLE INFINITO: Comparamos antes de guardar
                setRangoFechas((prev) => {
                  if (prev.fecha_inicio === nuevoInicio && prev.fecha_fin === nuevoFin) {
                    return prev // Si las fechas son exactamente iguales, cancela el render
                  }
                  return {
                    fecha_inicio: nuevoInicio,
                    fecha_fin: nuevoFin,
                  }
                })
              }}
              // ===============================================================

              eventContent={(eventInfo) => {
                const isPendiente = eventInfo.event.extendedProps?.status === "PENDIENTE"
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      p: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        fontSize: "0.75rem",
                        lineHeight: "1.1",
                        pr: 0.5,
                      }}
                    >
                      {eventInfo.event.title}
                    </Typography>
                    {isPendiente && (
                      <span
                        onClick={(e) => handleRequestDelete(e, eventInfo.event.id)}
                        style={{
                          cursor: "pointer",
                          color: "#d32f2f",
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          padding: "0px 4px",
                          backgroundColor: "rgba(255,255,255,0.8)",
                          borderRadius: "4px",
                          border: "1px solid #d32f2f",
                          lineHeight: "1",
                          display: "inline-block",
                        }}
                        title="Eliminar Tarea Planificada"
                      >
                        ×
                      </span>
                    )}
                  </Box>
                )
              }}
              eventDidMount={(info) => {
                if (info.event.extendedProps.tecnicoRequerido) {
                  info.el.style.borderLeft = "5px solid #d32f2f"
                }
              }}
            />
          </Box>
        </StyledRoot>

        {modalCrearOpen && (
          <ModalPlanificacionPaquetes
            open={modalCrearOpen}
            onClose={() => {
              setModalCrearOpen(false)
              setSlotSeleccionado(null)
              refetchEventos()
            }}
            slotPreseleccionado={slotSeleccionado}
          />
        )}

        {modalEjecucionOpen && eventoSeleccionado && (
          <ModalEjecucionTarea
            open={modalEjecucionOpen}
            onClose={() => {
              setModalEjecucionOpen(false)
              setEventoSeleccionado(null)
              refetchEventos()
            }}
            eventocodigo={eventoSeleccionado.id}
          />
        )}
      </div>
    </ThemeProvider>
  )
}

export default AgendaUnificada
